import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Calculator, ChevronDown, Globe2, Layers, Search } from 'lucide-react';
import { WsDropdown, WsSegmented } from 'src/stage/WsControls';
import { Skeleton, formatNumber } from 'src/stage/StageUi';
import {
  CATEGORIES, PRESETS, PRICING_SOURCE, RATE_CARD_DATE, cardCurrency, estimateMonth, findMarket, formatMoney, formatRate, marketForIso, marketForPhone,
} from './pricingModel';

const CURRENCIES = [{ id: 'INR', label: '₹ INR' }, { id: 'USD', label: '$ USD' }];
const effective = new Date(`${RATE_CARD_DATE}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

function defaultMarket(names, account, numbers) {
  const byAccount = account?.country ? marketForIso(account.country) : null;
  const byNumber = marketForPhone(numbers?.[0]?.phone_number);
  return [byNumber, byAccount, 'India'].find((name) => name && names.includes(name)) || names[0];
}

function Stepper({ id, label, hint, value, onChange }) {
  return (
    <label className="mb-field" htmlFor={id}>
      <span className="mb-field-label">{label}<small>{hint}</small></span>
      <input
        id={id}
        className="ws-input ws-num"
        type="number"
        min={0}
        step={100}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(Math.max(0, Math.floor(Number(event.target.value) || 0)))}
      />
    </label>
  );
}

function TierTable({ market, currency }) {
  const kinds = [['utility', 'Utility'], ['authentication', 'Authentication'], ['authentication_international', 'Authentication (international)']]
    .filter(([key]) => market.tiers?.[key]?.length);
  if (!kinds.length) return null;
  return (
    <div className="mb-tier-tables">
      {kinds.map(([key, label]) => (
        <table key={key} className="mb-table">
          <caption>{label}</caption>
          <thead><tr><th scope="col">Messages this month</th><th scope="col">Price each</th><th scope="col">Saving</th></tr></thead>
          <tbody>
            {market.tiers[key].map((tier) => (
              <tr key={tier.from}>
                <td className="ws-num">{tier.to === null ? `${formatNumber(tier.from)}+` : `${formatNumber(Math.max(1, tier.from))} – ${formatNumber(tier.to)}`}</td>
                <td className="ws-num">{formatRate(tier.rate, currency)}</td>
                <td className="ws-num">{tier.discount > 0 ? `${Math.round(tier.discount * 100)}% off` : 'List price'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
}

export default function PriceList({ card, account, usage }) {
  const numbers = usage?.data?.phone_numbers;
  const [currency, setCurrency] = useState(() => cardCurrency(account?.currency) || 'INR');
  const names = useMemo(() => (card.value?.currencies?.[currency]?.markets || []).map((row) => row.market), [card.value, currency]);
  const [marketName, setMarketName] = useState(null);
  const [counts, setCounts] = useState(PRESETS[0].values);
  const [preset, setPreset] = useState(PRESETS[0].id);
  const [numberCount, setNumberCount] = useState(() => Math.max(1, numbers?.length || 1));
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => { if (!marketName && names.length) setMarketName(defaultMarket(names, account, numbers)); }, [names, marketName, account, numbers]);
  useEffect(() => { const fromAccount = cardCurrency(account?.currency); if (fromAccount) setCurrency(fromAccount); }, [account?.currency]);
  useEffect(() => { if (numbers?.length) setNumberCount(numbers.length); }, [numbers?.length]);

  if (card.loading) return <div className="mb-prices"><Skeleton height={180} radius={20} /><Skeleton height={320} radius={20} /></div>;
  if (card.error || !card.value) {
    return (
      <section className="ws-card ws-card-pad" role="alert">
        <strong>The price list couldn’t be loaded.</strong>
        <p className="ws-muted">Reload the page, or see Meta’s rate cards directly.</p>
        <a className="ws-btn ws-btn--quiet ws-btn--sm" href={PRICING_SOURCE} target="_blank" rel="noreferrer">Meta’s price list<ArrowUpRight size={14} /></a>
      </section>
    );
  }

  const market = findMarket(card.value, currency, marketName) || findMarket(card.value, currency, 'India');
  const estimate = estimateMonth(market, { ...counts, numbers: numberCount });
  const setCount = (key) => (value) => { setCounts((current) => ({ ...current, [key]: value })); setPreset(null); };
  const accountMismatch = account?.currency && !cardCurrency(account.currency);
  const allRows = (card.value.currencies[currency]?.markets || []).filter((row) => row.market.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="mb-prices">
      <section className="ws-card ws-card-pad">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Globe2 size={17} /></span>
          <div className="ws-grow">
            <h2 className="ws-h3">Price per delivered message</h2>
            <p className="ws-muted">Meta’s official rates from {effective}. Pick where your customers are.</p>
          </div>
          <div className="mb-market-dd">
            <WsDropdown label="Customers in" icon={Globe2} value={market.market} options={names.map((name) => ({ value: name, label: name }))} onChange={setMarketName} align="right" />
          </div>
          <WsSegmented label="Currency" items={CURRENCIES} value={currency} onChange={setCurrency} />
        </div>
        {accountMismatch && <p className="wa-help">Your Meta account bills in {account.currency}. Prices here are shown in {currency}; see Meta’s list for {account.currency}.</p>}

        <div className="mb-rates">
          {CATEGORIES.map((category) => (
            <div key={category.id} className="mb-rate" style={{ '--c': category.color }}>
              <span className="mb-rate-label">{category.label}</span>
              <b className="ws-num">{formatRate(market[category.rate], currency)}</b>
              <small>{formatMoney((market[category.rate] || 0) * 1000, currency)} per 1,000</small>
              <p>{category.when}</p>
            </div>
          ))}
        </div>
        {market.authentication_international !== null && (
          <p className="wa-help">Businesses based outside {market.market} that send many one-time passwords here may pay the international authentication rate: {formatRate(market.authentication_international, currency)} each. Meta tells you 30 days before it applies.</p>
        )}
      </section>

      <section className="ws-card ws-card-pad mb-estimator" aria-labelledby="mb-estimate-title">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Calculator size={17} /></span>
          <div className="ws-grow">
            <h2 className="ws-h3" id="mb-estimate-title">Estimate a month</h2>
            <p className="ws-muted">Messages you expect to deliver in a month to customers in {market.market}.</p>
          </div>
          <div className="mb-presets" role="group" aria-label="Examples">
            {PRESETS.map((item) => (
              <button key={item.id} type="button" className={`mb-preset${preset === item.id ? ' is-active' : ''}`} aria-pressed={preset === item.id} onClick={() => { setCounts(item.values); setPreset(item.id); }}>{item.label}</button>
            ))}
          </div>
        </div>

        <div className="mb-estimator-grid">
          <div className="mb-fields">
            <Stepper id="mb-marketing" label="Marketing" hint="Offers and announcements" value={counts.marketing} onChange={setCount('marketing')} />
            <Stepper id="mb-utility" label="Utility" hint="Order updates, reminders" value={counts.utility} onChange={setCount('utility')} />
            <Stepper id="mb-authentication" label="Authentication" hint="One-time passwords" value={counts.authentication} onChange={setCount('authentication')} />
            <Stepper id="mb-service" label="Replies in chats" hint="Your normal messages" value={counts.service} onChange={setCount('service')} />
            <label className="mb-field" htmlFor="mb-numbers">
              <span className="mb-field-label">WhatsApp numbers<small>Each gets 1,000 free replies</small></span>
              <input id="mb-numbers" className="ws-input ws-num" type="number" min={1} value={numberCount} onChange={(event) => setNumberCount(Math.max(1, Math.floor(Number(event.target.value) || 1)))} />
            </label>
          </div>

          <div className="mb-result" aria-live="polite">
            <span className="mb-result-label">Estimated Meta charges</span>
            <b className="mb-result-total ws-num">{formatMoney(estimate.total, currency)}</b>
            <span className="ws-muted">per month · about {formatMoney(estimate.total / 30, currency)} a day</span>
            <span className="mb-result-bar" aria-hidden="true">
              {estimate.lines.map((line) => (estimate.total ? <i key={line.id} style={{ width: `${(line.cost / estimate.total) * 100}%`, background: CATEGORIES.find((c) => c.id === line.id).color }} /> : null))}
            </span>
            <ul className="mb-result-lines">
              {estimate.lines.map((line) => {
                const category = CATEGORIES.find((c) => c.id === line.id);
                return (
                  <li key={line.id}>
                    <span><i style={{ background: category.color }} aria-hidden="true" />{category.label}</span>
                    <small>
                      {line.id === 'service'
                        ? `${formatNumber(line.free)} free · ${formatNumber(line.billable)} charged`
                        : `${formatNumber(line.count)} × ${formatRate(line.rate, currency)}`}
                    </small>
                    <b className="ws-num">{formatMoney(line.cost, currency)}</b>
                  </li>
                );
              })}
            </ul>
            <p className="wa-help">An estimate at list prices with Meta’s volume discounts. Taxes and currency changes are not included; your invoice in Billing Hub is final.</p>
          </div>
        </div>
      </section>

      <section className="ws-card ws-card-pad">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Layers size={17} /></span>
          <div className="ws-grow">
            <h2 className="ws-h3">Bulk discounts in {market.market}</h2>
            <p className="ws-muted">Utility and authentication get cheaper after these monthly volumes, counted across all your WhatsApp accounts.</p>
          </div>
        </div>
        <TierTable market={market} currency={currency} />
      </section>

      <section className="ws-card ws-card-pad">
        <div className="mb-card-head">
          <span className="ws-icon-tile" aria-hidden="true"><Globe2 size={17} /></span>
          <div className="ws-grow">
            <h2 className="ws-h3">Every country</h2>
            <p className="ws-muted">{card.value.currencies[currency].markets.length} countries and regions. Countries not listed use “Other”.</p>
          </div>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" aria-expanded={showAll} onClick={() => setShowAll((value) => !value)}>
            {showAll ? 'Hide' : 'Show all'}<ChevronDown size={14} className={showAll ? 'mb-flip' : ''} />
          </button>
        </div>
        {showAll && (
          <>
            <label className="ws-searchbox mb-country-search">
              <Search size={15} aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a country or region" aria-label="Find a country or region" />
            </label>
            <div className="mb-table-scroll">
              <table className="mb-table is-wide">
                <thead>
                  <tr><th scope="col">Country or region</th>{CATEGORIES.map((category) => <th key={category.id} scope="col">{category.label}</th>)}<th scope="col">Auth. international</th></tr>
                </thead>
                <tbody>
                  {allRows.map((row) => (
                    <tr key={row.market} className={row.market === market.market ? 'is-picked' : ''}>
                      <th scope="row"><button type="button" className="mb-link" onClick={() => setMarketName(row.market)}>{row.market}</button></th>
                      {CATEGORIES.map((category) => <td key={category.id} className="ws-num">{formatRate(row[category.rate], currency)}</td>)}
                      <td className="ws-num">{row.authentication_international === null ? '—' : formatRate(row.authentication_international, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <p className="wa-help mb-foot">
        Source: <a href={PRICING_SOURCE} target="_blank" rel="noreferrer">Meta’s WhatsApp pricing and rate cards</a>, effective {effective}. Meta may change prices on 1 January, 1 April, 1 July and 1 October.
      </p>
    </div>
  );
}
